import React, { useState, useEffect, useCallback } from 'react';
import { APP_VERSION } from '../../version';
import { Star, X, MessageSquare } from 'lucide-react';

// Feedback Modal Component
const FeedbackModal = ({ isOpen, onClose, onSubmit, isPopup = false }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRating(0);
      setHoveredRating(0);
      setComment('');
      setSubmitted(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Bitte wähle eine Sternebewertung');
      return;
    }
    
    // Bei weniger als 5 Sternen ist ein Kommentar Pflicht
    if (rating < 5 && comment.trim() === '') {
      alert('Bitte gib uns einen kurzen Hinweis, was wir verbessern können.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ rating, comment });
      setSubmitted(true);
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert('Fehler beim Senden des Feedbacks. Bitte versuche es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={24} />
        </button>

        {submitted ? (
          // Success State
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🙏</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Vielen Dank!</h2>
            <p className="text-gray-600">Dein Feedback hilft uns, die App zu verbessern.</p>
          </div>
        ) : (
          // Form State
          <>
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">💬</div>
              <h2 className="text-xl font-bold text-gray-800">
                {isPopup ? 'Wie gefällt dir die App?' : 'Feedback geben'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                Deine Meinung ist uns wichtig (anonym)
              </p>
            </div>

            {/* Star Rating */}
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="focus:outline-none transform transition-transform hover:scale-110"
                >
                  <Star
                    size={40}
                    className={`transition-colors ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Rating Labels */}
            <div className="flex justify-between text-xs text-gray-400 mb-6 px-2">
              <span>Schlecht</span>
              <span>Ausgezeichnet</span>
            </div>

            {/* Comment Field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kommentar {rating > 0 && rating < 5 ? <span className="text-red-500">*</span> : '(optional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={rating > 0 && rating < 5 
                  ? "Was können wir verbessern? (Pflichtfeld bei weniger als 5 Sternen)"
                  : "Was können wir verbessern? Was gefällt dir besonders gut?"
                }
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  rating > 0 && rating < 5 && comment.trim() === '' 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-300'
                }`}
                rows={3}
                maxLength={500}
              />
              <div className="flex justify-between text-xs mt-1">
                {rating > 0 && rating < 5 && (
                  <span className="text-red-500">Bitte gib uns einen Hinweis zur Verbesserung</span>
                )}
                <span className="text-gray-400 ml-auto">{comment.length}/500</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || rating === 0}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                rating === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Wird gesendet...
                </span>
              ) : (
                'Feedback absenden'
              )}
            </button>

            {isPopup && (
              <button
                onClick={onClose}
                className="w-full mt-3 py-2 text-gray-500 hover:text-gray-700 text-sm"
              >
                Später erinnern
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Feedback Button Component (for header)
const FeedbackButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors whitespace-nowrap"
      title="Feedback geben"
    >
      <MessageSquare size={17} />
      <span>Feedback</span>
    </button>
  );
};

// Custom hook for Feedback functionality
const useFeedback = (paymentApiUrl, wacheId) => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackVersion, setFeedbackVersion] = useState(null);

  // Storage keys
  const FEEDBACK_GIVEN_KEY = 'fw_kantine_feedback_given';
  const FEEDBACK_VERSION_KEY = 'fw_kantine_feedback_version';
  const FEEDBACK_DISMISSED_KEY = 'fw_kantine_feedback_dismissed';

  // Check if popup should show
  // DEAKTIVIERT: Automatisches Popup ist deaktiviert, nur manuelles Feedback über Button
  const checkPopupTrigger = useCallback(() => {
    // Immer false zurückgeben - kein automatisches Popup mehr
    return false;
    
    /* ALTE LOGIK (deaktiviert):
    const feedbackGiven = localStorage.getItem(FEEDBACK_GIVEN_KEY);
    const storedVersion = localStorage.getItem(FEEDBACK_VERSION_KEY);
    const dismissed = sessionStorage.getItem(FEEDBACK_DISMISSED_KEY);

    // Don't show if already dismissed this session
    if (dismissed) return false;

    // Don't show if feedback given for current version
    if (feedbackGiven && storedVersion === String(feedbackVersion)) {
      return false;
    }

    // If version changed (reset by developer), allow new popup
    if (feedbackVersion !== null && storedVersion !== String(feedbackVersion)) {
      return true;
    }

    // First time user - show popup
    if (!feedbackGiven) {
      return true;
    }

    return false;
    */
  }, [feedbackVersion]);

  // Trigger popup before first order
  const triggerPopupBeforeOrder = useCallback(() => {
    if (checkPopupTrigger()) {
      setShowFeedbackPopup(true);
      return true; // Popup shown
    }
    return false; // No popup
  }, [checkPopupTrigger]);

  // Submit feedback to Payment API
  const submitFeedback = useCallback(async ({ rating, comment }) => {
    // Generate anonymous ID (hash of timestamp + random)
    const anonymousId = btoa(Date.now() + Math.random().toString(36)).slice(0, 16);
    
    // Get subdomain from current URL or wacheId
    const subdomain = wacheId || window.location.hostname.split('.')[0];

    const feedbackData = {
      rating,
      comment: comment || '',
      anonymous_id: anonymousId,
      subdomain,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      app_version: APP_VERSION
    };

    // Try to send to Payment API
    if (paymentApiUrl) {
      try {
        const response = await fetch(`${paymentApiUrl}/api/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(feedbackData),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.warn('Could not send to Payment API, storing locally:', error);
        // Fallback: Store locally
        const localFeedback = JSON.parse(localStorage.getItem('fw_kantine_local_feedback') || '[]');
        localFeedback.push(feedbackData);
        localStorage.setItem('fw_kantine_local_feedback', JSON.stringify(localFeedback));
      }
    }

    // Mark as given
    localStorage.setItem(FEEDBACK_GIVEN_KEY, 'true');
    localStorage.setItem(FEEDBACK_VERSION_KEY, String(feedbackVersion || 1));
  }, [paymentApiUrl, wacheId, feedbackVersion]);

  // Dismiss popup for this session
  const dismissPopup = useCallback(() => {
    sessionStorage.setItem(FEEDBACK_DISMISSED_KEY, 'true');
    setShowFeedbackPopup(false);
  }, []);

  // Load feedback version from settings
  const loadFeedbackVersion = useCallback(async (apiBaseUrl) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/feedback/version`);
      if (response.ok) {
        const data = await response.json();
        setFeedbackVersion(data.version || 1);
      }
    } catch (error) {
      // Default to version 1 if can't fetch
      setFeedbackVersion(1);
    }
  }, []);

  return {
    showFeedbackModal,
    setShowFeedbackModal,
    showFeedbackPopup,
    setShowFeedbackPopup,
    triggerPopupBeforeOrder,
    submitFeedback,
    dismissPopup,
    loadFeedbackVersion,
    FeedbackModal,
    FeedbackButton,
  };
};

export { FeedbackModal, FeedbackButton, useFeedback };
export default FeedbackModal;
